import type { UseQueryResult } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import BandwidthCountsTable, { type CountsByCustomerRow } from './BandwidthCountsTable';
import { SectionLabel } from '../kpi-dashboard/KpiDashboardContent';
import BandwidthGroupUserTable from './BandwidthGroupUserTable';
import BandwidthHoursAndCountOverTimeChart from './BandwidthHoursAndCountOverTimeChart';
import BandwidthActivityAllocationTable from './BandwidthActivityAllocationTable';
import BandwidthTeamResourceTable from './BandwidthTeamResourceTable';
import { buildAllocationViewsFromDashboard } from './bandwidthAllocation';
import type { BandwidthCrossFilterSlice } from './bandwidthCrossFilter';
import type { BandwidthDashboardData } from './bandwidthDashboardTypes';

export interface BandwidthCountTimeGridProps {
  crossFilter: BandwidthCrossFilterSlice;
  onCrossFilterChange: (next: BandwidthCrossFilterSlice) => void;
  /** Page filter Assigned To — drives API + Active Filters chip; table row clicks update this. */
  assigneeUserIds: string[] | null;
  onAssigneeUserToggle: (userId: string) => void;
  /** Page filter Customer — same as Filters menu; drives highlight when no customer cross-filter. */
  pageCustomerId: string | null;
  /** Single selected activity type from page filter (Type), for row highlight. */
  selectedActivityType: string | null;
  onActivityTypeToggle: (activityType: string) => void;
  customers: Array<{ id: string; accountName: string }>;
  dashboardQuery: UseQueryResult<BandwidthDashboardData, Error>;
  exportDateFrom: string;
  exportDateTo: string;
}

function resolveAssigneeDisplayName(
  userId: string,
  data: BandwidthDashboardData | undefined
): string | undefined {
  for (const g of data?.groupUserBreakdown ?? []) {
    const u = g.users.find((x) => x.userId === userId);
    if (u) return u.userName;
  }
  for (const c of data?.countsByCustomer ?? []) {
    const u = c.users?.find((x) => x.userId === userId);
    if (u) return u.userName;
  }
  return undefined;
}

function sliceSubtitle(
  slice: BandwidthCrossFilterSlice,
  customers: Array<{ id: string; accountName: string }>,
  data: BandwidthDashboardData | undefined,
  assigneeUserIds: string[] | null,
  pageCustomerId: string | null
): string | undefined {
  const parts: string[] = [];
  if (assigneeUserIds?.length) {
    parts.push(
      assigneeUserIds
        .map((id) => resolveAssigneeDisplayName(id, data) ?? id)
        .join(', ')
    );
  }
  if (pageCustomerId) {
    parts.push(customers.find((c) => c.id === pageCustomerId)?.accountName ?? pageCustomerId);
  }
  if (slice?.kind === 'group') {
    parts.push(data?.groupUserBreakdown?.find((x) => x.groupId === slice.id)?.groupName ?? 'Teams');
  }
  if (slice?.kind === 'customer' && slice.id !== pageCustomerId) {
    parts.push(customers.find((c) => c.id === slice.id)?.accountName ?? slice.id);
  }
  if (parts.length === 0) return undefined;
  return `Filtered: ${parts.join(' · ')}`;
}

const BandwidthCountTimeGrid: React.FC<BandwidthCountTimeGridProps> = ({
  crossFilter,
  onCrossFilterChange,
  assigneeUserIds,
  onAssigneeUserToggle,
  pageCustomerId,
  selectedActivityType,
  onActivityTypeToggle,
  customers,
  dashboardQuery,
  exportDateFrom,
  exportDateTo,
}) => {
  const baseData = dashboardQuery.data;
  const { isLoading: baseLoading, isError: baseIsError, error: baseError } = dashboardQuery;

  const countRows: CountsByCustomerRow[] = useMemo(() => {
    if (!baseData?.countsByCustomer) return [];
    return baseData.countsByCustomer.map((r) => ({
      customer: r.customer,
      customerId: r.customerId,
      totalActivity: r.totalActivityCount,
      timeMinutes: r.timeSpentMinutes,
      timeHrs: r.timeSpentHours,
      avgHrsOnActivity: r.avgHrsOnActivity,
      users: (r.users ?? []).map((u) => ({
        userId: u.userId,
        userName: u.userName,
        totalActivity: u.totalActivityCount,
        timeHrs: u.timeSpentHours,
        avgHrsOnActivity: u.avgHrsOnActivity,
        isInactiveUser: u.isInactiveUser,
      })),
    }));
  }, [baseData?.countsByCustomer]);

  const chartSubtitle = useMemo(
    () => sliceSubtitle(crossFilter, customers, baseData, assigneeUserIds, pageCustomerId),
    [crossFilter, customers, baseData, assigneeUserIds, pageCustomerId]
  );

  const allocationViews = useMemo(() => {
    if (!baseData) {
      return { teamResourceRows: [], activityRows: [], scopeCapacityHours: 0 };
    }
    return buildAllocationViewsFromDashboard(baseData, exportDateFrom, exportDateTo);
  }, [baseData, exportDateFrom, exportDateTo]);

  const customerRowHighlightId = useMemo(
    () => (crossFilter?.kind === 'customer' ? crossFilter.id : pageCustomerId),
    [crossFilter, pageCustomerId]
  );

  const handleCustomerCrossFilter = useCallback(
    (customerId: string) => {
      onCrossFilterChange(
        crossFilter?.kind === 'customer' && crossFilter.id === customerId
          ? null
          : { kind: 'customer', id: customerId }
      );
    },
    [crossFilter, onCrossFilterChange]
  );

  const handleTeamCrossFilter = useCallback(
    (teamId: string) => {
      onCrossFilterChange(
        crossFilter?.kind === 'group' && crossFilter.id === teamId
          ? null
          : { kind: 'group', id: teamId }
      );
    },
    [crossFilter, onCrossFilterChange]
  );

  if (baseLoading && !baseData) {
    return (
      <div className="flex min-h-[280px] items-center justify-center rounded-2xl border border-indigo-100/80 bg-white/90 p-12 text-sm text-slate-600 shadow-sm shadow-indigo-900/5 ring-1 ring-slate-950/[0.03]">
        Loading bandwidth dashboard…
      </div>
    );
  }

  if (baseIsError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50/90 p-6 text-sm text-red-800 shadow-sm">
        {baseError instanceof Error ? baseError.message : 'Could not load bandwidth dashboard.'}
      </div>
    );
  }

  const groups = baseData?.groupUserBreakdown ?? [];
  const periodOverTimeTrend =
    baseData?.periodOverTimeTrend ?? { granularity: 'month' as const, points: [] };

  return (
    <div className="flex flex-col">
      <section className="mb-6 w-full" aria-labelledby="dashboard-activity-kpis-heading">
        <SectionLabel id="dashboard-activity-kpis-heading">teams & customers KPIs</SectionLabel>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5 lg:items-stretch">
          <BandwidthGroupUserTable
            title="Teams"
            groups={groups}
            capacityDeductionByGroupUser={baseData?.capacityDeductionByGroupUser}
            crossFilter={crossFilter}
            onSelectSlice={onCrossFilterChange}
            assigneeUserIds={assigneeUserIds}
            onAssigneeUserClick={onAssigneeUserToggle}
            loading={baseLoading && !baseData}
            exportDateFrom={exportDateFrom}
            exportDateTo={exportDateTo}
          />
          <BandwidthCountsTable
            title="Customer"
            subtitle={chartSubtitle}
            rows={countRows}
            selectedCustomerId={customerRowHighlightId}
            selectedUserIds={assigneeUserIds}
            onRowClick={(customerId) => handleCustomerCrossFilter(customerId)}
            onUserClick={(userId) => onAssigneeUserToggle(userId)}
            exportDateFrom={exportDateFrom}
            exportDateTo={exportDateTo}
          />
        </div>
      </section>
      <section className="mb-6 w-full" aria-labelledby="dashboard-allocation-heading">
        <SectionLabel id="dashboard-allocation-heading">bandwidth allocation</SectionLabel>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5 lg:items-stretch">
          <BandwidthTeamResourceTable
            rows={allocationViews.teamResourceRows}
            loading={baseLoading && !baseData}
            subtitle={chartSubtitle}
            exportDateFrom={exportDateFrom}
            exportDateTo={exportDateTo}
            crossFilter={crossFilter}
            onTeamClick={handleTeamCrossFilter}
            assigneeUserIds={assigneeUserIds}
            onResourceClick={onAssigneeUserToggle}
          />
          <div className="min-w-0">
            <BandwidthActivityAllocationTable
              rows={allocationViews.activityRows}
              loading={baseLoading && !baseData}
              subtitle={chartSubtitle}
              exportDateFrom={exportDateFrom}
              exportDateTo={exportDateTo}
              selectedActivityType={selectedActivityType}
              onActivityClick={onActivityTypeToggle}
            />
          </div>
        </div>
      </section>
      <section className="mb-6 w-full">
        <div className="w-full min-h-0">
          <BandwidthHoursAndCountOverTimeChart
            title="Time & activity over period"
            subtitle={chartSubtitle}
            periodOverTimeTrend={periodOverTimeTrend}
            emptyHint={baseLoading ? 'Loading…' : 'No data for the selected filters.'}
          />
        </div>
      </section>
    </div>
  );
};

export default BandwidthCountTimeGrid;
